import { useCallback, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAuthCore } from '../../core/modules/auth';
import { ERROR } from '../../core/constants/errorCode';
import { WELCOME } from '../../core/router/routes';
import { useGlobalCore } from '../../core/modules/global';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import ReactPlayer from 'react-player';
import { Mixpanel } from '../../MixPanel';
import TermsNotice from '../components/auth/TermsNotice';

const SignIn = () => {
  const { token, errorCode, googleLogin } = useAuthCore();
  const { closeAllDialog } = useGlobalCore();

  const [googleLoginError, setGoogleLoginError] = useState(false);

  const errorMessage = useMemo(() => {
    if (errorCode) {
      if (errorCode === ERROR.ERR_401) {
        return 'La cuenta no pudo ser identificada';
      } else {
        return 'Un error inexcperado a ocurrido, intente de nuevo en unos minutos';
      }
    }
  }, [errorCode]);

  const onGoogleLogin = useCallback((credentials: CredentialResponse) => {
    if (credentials.credential) {
      googleLogin({ token: credentials.credential }, ({ id }) => {
        closeAllDialog();
        if (process.env.REACT_APP_MIXPANEL_TOKEN) {
          Mixpanel.identify(id);
          Mixpanel.track('Successful login');
        }
      });
    }
  }, [closeAllDialog, googleLogin]);

  if (token) {
    return <Navigate to={WELCOME} replace />;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center md:items-start sm:justify-center md:justify-start flex-1 min-w-0">
      <Paper className="h-full sm:h-auto md:flex md:items-center md:justify-end w-full sm:w-auto md:h-full md:w-1/2 py-8 px-16 sm:p-48 md:p-64 sm:rounded-2xl md:rounded-none sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1">
        <div className="w-full max-w-320 sm:w-320 mx-auto sm:mx-0">
          <div className="flex justify-center md:justify-start">
            <img
              className="h-48"
              src={`${process.env.PUBLIC_URL}/assets/images/logo/vlogo.png`}
              alt="logo"
            />
          </div>

          <div className="mt-32 flex justify-center md:justify-start">
            <GoogleLogin
              onSuccess={onGoogleLogin}
              size="large"
              shape="square"
              width="320"
              locale="es"
              onError={() => {
                setGoogleLoginError(true);
              }}
            />
          </div>

          <TermsNotice />

          {errorMessage && (
            <div className="text-center mt-8">
              <Typography variant="caption" className="text-red-500">
                {errorMessage}
              </Typography>
            </div>
          )}
          {googleLoginError && (
            <div className="text-center mt-8">
              <Typography variant="caption" className="text-red-500">
                Ocurrió un error en el login de Google
              </Typography>
            </div>
          )}
        </div>
      </Paper>

      <Box
        className="relative hidden md:flex flex-auto items-center justify-center overflow-hidden"
        sx={{ backgroundColor: 'primary.main' }}
      >
        <div className="z-10 relative w-full">
          <ReactPlayer
            url={`${process.env.PUBLIC_URL}/assets/welcome.mp4`}
            width="100%"
            height="100%"
            volume={0}
            playing
            loop
          />
        </div>
      </Box>
    </div>
  );
};

export default SignIn;
